import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, Clock4, Sun, Send } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Reveal, FadeIn } from "../components/Motion";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const BATCHES = [
  { icon: Sun, name: "Regular Morning", time: "Mon – Fri · 9:00 AM – 12:00 PM", note: "Ideal for full-time students" },
  { icon: Clock4, name: "Evening Batch", time: "Mon – Fri · 4:00 PM – 6:00 PM", note: "For college students & homemakers" },
  { icon: CalendarDays, name: "Weekend Batch", time: "Sat & Sun · 10:00 AM – 1:00 PM", note: "For working professionals" },
];

const FAQS = [
  {
    q: "Are the certificates recognized?",
    a: "Yes. Our teacher-training programs follow a government-recognized framework aligned with NCTE guidelines, and our certificates are accepted by private and aided schools across Karnataka.",
  },
  {
    q: "Do you really provide placement assistance?",
    a: "100% placement assistance is part of every program. We connect graduates with partner schools in Mandya, Mysuru and surrounding districts, and support you through interviews.",
  },
  {
    q: "I am working. Can I still study?",
    a: "Absolutely. Evening and weekend batches are designed for working professionals, homemakers and college students.",
  },
  {
    q: "Are there fee concessions?",
    a: "Yes. As a charitable trust we offer concessions for rural, underprivileged and single-parent candidates. Speak to our counsellor — finances should never stop a calling.",
  },
  {
    q: "How do I apply?",
    a: "Fill the application form on this page, call +91 79750 94721, or visit us at Guthalu Road, Mandya (Mon–Sat, 9 AM – 6 PM).",
  },
];

const EDUCATION_OPTIONS = ["SSLC / 10th", "PUC / 12th", "Graduate", "Post-Graduate", "Other"];
const BATCH_OPTIONS = ["Regular Morning", "Evening Batch", "Weekend Batch"];

const FALLBACK_COURSES = [
  { id: "fb-ntt", slug: "ntt", title: "Nursery Teacher Training (NTT)" },
  { id: "fb-nst", slug: "nst", title: "Nursery School Teacher (NST)" },
  { id: "fb-dntt", slug: "dntt", title: "Diploma in Nursery Teacher Training (DNTT)" },
  { id: "fb-ptt", slug: "ptt", title: "Primary Teacher Training (PTT)" },
  { id: "fb-spoken-english", slug: "spoken-english", title: "Spoken English & Grammar" },
  { id: "fb-ielts", slug: "ielts", title: "IELTS Coaching" },
  { id: "fb-vocational", slug: "vocational", title: "Community Vocational Programs" },
];

export default function Admissions() {
  const [params] = useSearchParams();
  const [courses, setCourses] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "", phone: "", email: "", course: params.get("course") || "", education: "", batch: "", message: "",
  });

  useEffect(() => {
    api
      .get("/courses")
      .then((r) => setCourses(r.data?.length ? r.data : FALLBACK_COURSES))
      .catch(() => setCourses(FALLBACK_COURSES));
  }, []);

  useEffect(() => {
    const c = params.get("course");
    if (c) setForm((f) => ({ ...f, course: c }));
  }, [params]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.course) {
      toast.error("Please fill your name, phone and choose a course.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/applications", { ...form, email: form.email || null, message: form.message || null });
      toast.success("Application received! Our counsellor will call you within one working day.");
      setForm({ name: "", phone: "", email: "", course: "", education: "", batch: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const selectCls =
    "flex h-11 w-full rounded-md border border-input bg-white px-3 text-sm text-forest-deep focus:outline-none focus:ring-2 focus:ring-ring";

  return (
    <div data-testid="admissions-page" className="pt-32 lg:pt-40">
      <section className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <FadeIn>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-terracotta-dark">Admissions 2026 – 27</p>
        </FadeIn>
        <h1 className="mt-4 max-w-3xl font-serif text-4xl font-bold leading-[1.08] tracking-tight text-forest-deep sm:text-5xl lg:text-6xl">
          <Reveal y={24}>Your seat is</Reveal>
          <Reveal y={24} delay={0.1}>
            <span className="italic text-terracotta">waiting for you.</span>
          </Reveal>
        </h1>
        <Reveal delay={0.2}>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-stone-600 sm:text-lg">
            Admissions are open for all teacher-training, language and vocational programs. Concessions available for
            rural and underprivileged candidates.
          </p>
        </Reveal>
      </section>

      {/* Batches */}
      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8" data-testid="batches-section">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {BATCHES.map((b, i) => (
            <Reveal key={b.name} delay={i * 0.1}>
              <div data-testid={`batch-card-${b.name.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="h-full rounded-3xl border border-forest/10 bg-white p-7 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-forest/5">
                <b.icon className="h-7 w-7 text-terracotta" />
                <h3 className="mt-4 font-serif text-2xl font-semibold text-forest-deep">{b.name}</h3>
                <p className="mt-2 font-display text-sm font-medium text-forest">{b.time}</p>
                <p className="mt-1 text-sm text-stone-500">{b.note}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Application form */}
      <section className="bg-linen/70 py-20 lg:py-28" data-testid="application-section">
        <div className="mx-auto grid max-w-7xl grid-cols-1 gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:px-8">
          <Reveal className="lg:col-span-5">
            <p className="font-mono text-xs uppercase tracking-[0.25em] text-terracotta-dark">Apply Now</p>
            <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight text-forest-deep sm:text-4xl">
              Five minutes today.<br />A career for a lifetime.
            </h2>
            <p className="mt-5 max-w-md text-sm leading-relaxed text-stone-600">
              Submit the form and our counsellor will reach out within one working day to discuss batches, fees and the
              concessions you may be eligible for.
            </p>
            <ul className="mt-8 space-y-3 text-sm text-stone-600">
              {["No application fee", "Counsellor callback within 1 working day", "Fee concessions for eligible candidates"].map((t) => (
                <li key={t} className="flex items-center gap-3">
                  <span className="h-1.5 w-1.5 rounded-full bg-terracotta" /> {t}
                </li>
              ))}
            </ul>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-7">
            <form onSubmit={submit} data-testid="application-form" className="rounded-3xl border border-forest/10 bg-white p-8 shadow-sm sm:p-10">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <Label htmlFor="app-name">Full Name *</Label>
                  <Input id="app-name" data-testid="application-name-input" value={form.name} onChange={set("name")} placeholder="Your full name" className="mt-2 h-11" />
                </div>
                <div>
                  <Label htmlFor="app-phone">Mobile Number *</Label>
                  <Input id="app-phone" data-testid="application-phone-input" value={form.phone} onChange={set("phone")} placeholder="+91 ..." className="mt-2 h-11" />
                </div>
                <div>
                  <Label htmlFor="app-email">Email</Label>
                  <Input id="app-email" type="email" data-testid="application-email-input" value={form.email} onChange={set("email")} placeholder="you@example.com" className="mt-2 h-11" />
                </div>
                <div>
                  <Label htmlFor="app-course">Course *</Label>
                  <select id="app-course" data-testid="application-course-select" value={form.course} onChange={set("course")} className={`mt-2 ${selectCls}`}>
                    <option value="">Select a course</option>
                    {courses.map((c) => (
                      <option key={c.id} value={c.slug}>{c.title}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="app-education">Highest Education</Label>
                  <select id="app-education" data-testid="application-education-select" value={form.education} onChange={set("education")} className={`mt-2 ${selectCls}`}>
                    <option value="">Select education</option>
                    {EDUCATION_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="app-batch">Preferred Batch</Label>
                  <select id="app-batch" data-testid="application-batch-select" value={form.batch} onChange={set("batch")} className={`mt-2 ${selectCls}`}>
                    <option value="">Select batch</option>
                    {BATCH_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="app-message">Anything you'd like us to know?</Label>
                  <Textarea id="app-message" data-testid="application-message-input" value={form.message} onChange={set("message")} placeholder="Questions about fees, batches, concessions..." className="mt-2 min-h-[100px]" />
                </div>
                <input type="text" name="website" value={form.website || ""} onChange={set("website")} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" data-testid="application-honeypot" />
              </div>
              <Button type="submit" disabled={submitting} data-testid="application-submit-button" className="mt-8 w-full rounded-full bg-forest py-6 font-display text-base text-cream hover:bg-forest-deep disabled:opacity-60">
                {submitting ? "Submitting..." : (<>Submit Application <Send className="ml-2 h-4 w-4" /></>)}
              </Button>
            </form>
          </Reveal>
        </div>
      </section>

      {/* FAQs */}
      <section className="mx-auto max-w-4xl px-4 py-24 sm:px-6 lg:px-8" data-testid="faq-section">
        <Reveal>
          <p className="text-center font-mono text-xs uppercase tracking-[0.25em] text-terracotta-dark">Questions, Answered</p>
          <h2 className="mt-4 text-center font-serif text-3xl font-semibold text-forest-deep sm:text-4xl">Before you ask.</h2>
        </Reveal>
        <Reveal delay={0.15}>
          <Accordion type="single" collapsible className="mt-10" data-testid="faq-accordion">
            {FAQS.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-forest/10" data-testid={`faq-item-${i}`}>
                <AccordionTrigger className="text-left font-display text-base font-semibold text-forest-deep hover:text-terracotta">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm leading-relaxed text-stone-600">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Reveal>
      </section>
    </div>
  );
}
